/* ── Same Skies — Star Field ────────────────────────────────────────────────
   Canvas-based twinkling star field for elements with .ss-starfield class.
   "Same Skies" — two people, different continents, same stars.

   Performance: single rAF loop per canvas, throttled resize, pauses when
   the element is not in the viewport (IntersectionObserver).
   ──────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function createStarfield(container) {
    var canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    container.insertBefore(canvas, container.firstChild);

    var ctx = canvas.getContext('2d');
    var stars = [];
    var raf = null;
    var active = true;

    /* ── Resize: fit canvas to container, re-seed stars ── */
    function resize() {
      var w = container.offsetWidth;
      var h = container.offsetHeight;
      if (!w || !h) return;
      canvas.width  = w;
      canvas.height = h;
      initStars(w, h);
    }

    /* ── Seed star positions, sizes, and individual twinkle phases ── */
    function initStars(w, h) {
      stars = [];
      /* ~1 star per 5000px² — feels natural, not overwhelming */
      var count = Math.max(35, Math.min(Math.floor((w * h) / 5000), 130));

      for (var i = 0; i < count; i++) {
        var size = Math.random();
        /* Three tiers: tiny (most), mid, bright accent (few) */
        var r = size < 0.70 ? Math.random() * 0.5 + 0.25   /* tiny   0.25–0.75 */
              : size < 0.92 ? Math.random() * 0.6 + 0.75   /* mid    0.75–1.35 */
              :               Math.random() * 0.5 + 1.4;   /* bright 1.4–1.9  */

        stars.push({
          x:     Math.random() * w,
          y:     Math.random() * h,
          r:     r,
          /* Base alpha — bright stars are more visible */
          base:  r < 0.75 ? Math.random() * 0.28 + 0.08
               : r < 1.35 ? Math.random() * 0.32 + 0.18
               :             Math.random() * 0.25 + 0.38,
          /* Twinkle: phase offset so all stars don't pulse together */
          phase: Math.random() * Math.PI * 2,
          /* Speed: brighter stars twinkle slower (more dramatic) */
          freq:  r < 1.0 ? Math.random() * 0.4 + 0.25
               :           Math.random() * 0.2 + 0.1,
          /* Twinkle depth: how much alpha swings */
          depth: r < 0.75 ? 0.06 : r < 1.35 ? 0.12 : 0.18,
        });
      }
    }

    /* ── Draw one frame ── */
    function draw(ts) {
      if (!active) { raf = null; return; }
      var t = ts * 0.001;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (var i = 0; i < stars.length; i++) {
        var s = stars[i];
        var alpha = s.base + Math.sin(t * s.freq * Math.PI * 2 + s.phase) * s.depth;
        alpha = Math.max(0.03, Math.min(alpha, 0.92));

        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(250,248,244,' + alpha.toFixed(3) + ')';
        ctx.fill();

        /* Cross-sparkle on the brightest stars only */
        if (s.r > 1.55 && alpha > 0.45) {
          var arm = s.r * 2.2;
          ctx.strokeStyle = 'rgba(250,248,244,' + (alpha * 0.35).toFixed(3) + ')';
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(s.x - arm, s.y);
          ctx.lineTo(s.x + arm, s.y);
          ctx.moveTo(s.x, s.y - arm);
          ctx.lineTo(s.x, s.y + arm);
          ctx.stroke();
        }
      }

      raf = requestAnimationFrame(draw);
    }

    /* ── Pause when off-screen ── */
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        active = entries[0].isIntersecting;
        if (active && !raf) raf = requestAnimationFrame(draw);
      }, { threshold: 0 });
      io.observe(container);
    }

    /* ── Throttled resize ── */
    var rsz;
    window.addEventListener('resize', function () {
      clearTimeout(rsz);
      rsz = setTimeout(resize, 150);
    }, { passive: true });

    /* ── ResizeObserver on the container ── handles containers that are
       position:fixed or flex-laid-out where offsetWidth/Height can be 0
       at DOMContentLoaded. Without this, the gift-builder .gb-wrap
       starfield never seeds because resize() bails on 0 dimensions
       and a window-resize event never fires. */
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () {
        clearTimeout(rsz);
        rsz = setTimeout(resize, 60);
      });
      ro.observe(container);
    }

    resize();
    raf = requestAnimationFrame(draw);
  }

  function init() {
    document.querySelectorAll('.ss-starfield').forEach(createStarfield);
    document.querySelectorAll('.ss-star-overlay').forEach(createStarfield);
  }

  /* Use window load (full layout/paint complete) instead of DOMContentLoaded
     so position:fixed containers like .gb-wrap have non-zero dimensions
     when first measured. ResizeObserver above is the belt-and-braces. */
  if (document.readyState === 'complete') {
    init();
  } else {
    window.addEventListener('load', init);
  }

})();
